import { create } from "zustand";
import { authApi } from "../lib/api/auth";
import { toast } from "react-hot-toast";
import axios from "../lib/axios";

const AUTH_SESSION_MARKER = "shaShaAuthSession";

const markAuthSession = () => {
	try {
		localStorage.setItem(AUTH_SESSION_MARKER, "true");
	} catch { /* noop */ }
};

const clearAuthSessionMarker = () => {
	try {
		localStorage.removeItem(AUTH_SESSION_MARKER);
	} catch { /* noop */ }
};

const hasAuthSessionMarker = () => {
	try {
		return localStorage.getItem(AUTH_SESSION_MARKER) === "true";
	} catch {
		return false;
	}
};

export const useUserStore = create((set, get) => ({
	user: null,
	loading: false,
	checkingAuth: true,

	signup: async ({ firstName, lastName, email, password, confirmPassword }) => {
		set({ loading: true });

		if (password !== confirmPassword) {
			set({ loading: false });
			return toast.error("Passwords do not match");
		}

		try {
			const user = await authApi.signup({ firstName, lastName, email, password });
			markAuthSession();
			set({ user, loading: false, checkingAuth: false });
		} catch (error) {
			set({ loading: false, checkingAuth: false });
			const message = error?.response?.data?.message || error?.message || "An error occurred";
			toast.error(message);
		}
	},
	login: async (email, password) => {
		set({ loading: true });
		console.log('🔐 מנסה להתחבר עם:', email);

		try {
			const response = await authApi.login({ email, password });
			console.log('📋 תגובה מהשרת:', response);

			// השרת מחזיר { success: true, user: {...} }
			if (response.success && response.user) {
				console.log('✅ התחברות מוצלחת:', response.user.email);
				markAuthSession();
				set({ user: response.user, loading: false, checkingAuth: false });
				try { get().startTokenRefreshScheduler(); } catch { /* noop */ }
				
				// עדכן מצב הסל לאחר התחברות מוצלחת
				try {
					const { useCartStore } = await import("./useCartStore");
					useCartStore.getState().setUserLoginStatus(true);
				} catch (error) {
					console.error('Error updating cart status:', error);
				}
			} else {
				console.log('❌ תגובה לא תקינה:', response);
				markAuthSession();
				set({ user: response, loading: false, checkingAuth: false });
			}
		} catch (error) {
			console.log('❌ שגיאה בהתחברות:', error?.message);
			console.log('📋 פרטי השגיאה:', error?.response?.data);
			set({ loading: false, checkingAuth: false });
			const message = error?.response?.data?.message || error?.message || "An error occurred";
			toast.error(message);
		}
	},

	logout: async () => {
		try {
			await authApi.logout();
			console.log('✅ התנתק בהצלחה');
		} catch (error) {
			console.log('❌ שגיאה בהתנתקות:', error?.message);
		} finally {
			// תמיד נניקה את הטוקן והמשתמש
			document.cookie = 'accessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
			clearAuthSessionMarker();
			set({ user: null, checkingAuth: false });
			try { get().stopTokenRefreshScheduler(); } catch { /* noop */ }
			
			// עדכן מצב הסל לאחר התנתקות
			try {
				const { useCartStore } = await import("./useCartStore");
				useCartStore.getState().setUserLoginStatus(false);
			} catch (error) {
				console.error('Error updating cart status:', error);
			}
		}
	},

	checkAuth: async () => {
		set({ checkingAuth: true });
		console.log('🔍 בודק אותנטיקציה...');

		if (!hasAuthSessionMarker()) {
			console.log('ℹ️ אין session מקומי, מדלג על בדיקת auth');
			set({ user: null, checkingAuth: false });
			try { get().stopTokenRefreshScheduler(); } catch { /* noop */ }
			return null;
		}

		try {
			const user = await authApi.checkAuth();
			console.log('✅ משתמש מחובר:', user.email);
			markAuthSession();
			set({ user, checkingAuth: false });
			try { get().startTokenRefreshScheduler(); } catch { /* noop */ }
			
			// עדכן מצב הסל לאחר אימות המשתמש
			try {
				const { useCartStore } = await import("./useCartStore");
				useCartStore.getState().setUserLoginStatus(true);
			} catch (error) {
				console.error('Error updating cart status:', error);
			}
			
			return user;
		} catch (error) {
			console.log('❌ בדיקת אותנטיקציה נכשלה:', error?.response?.status);

			if (error?.response?.status === 401) {
				try {
					await authApi.refreshToken();
					const user = await authApi.checkAuth();
					console.log('✅ התחברות שוחזרה אחרי רענון טוקן:', user.email);
					markAuthSession();
					set({ user, checkingAuth: false });
					try { get().startTokenRefreshScheduler(); } catch { /* noop */ }
					try {
						const { useCartStore } = await import("./useCartStore");
						useCartStore.getState().setUserLoginStatus(true);
					} catch (cartError) {
						console.error('Error updating cart status:', cartError);
					}
					return user;
				} catch (refreshError) {
					console.log('❌ רענון טוקן נכשל:', refreshError?.response?.status);
					clearAuthSessionMarker();
				}
			}

			// ניקוי הטוקן במקרה של שגיאה
			if (error?.response?.status === 401) {
				document.cookie = 'accessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
			}
			clearAuthSessionMarker();
			set({ user: null, checkingAuth: false });
			try { get().stopTokenRefreshScheduler(); } catch { /* noop */ }
			
			// עדכן מצב הסל כשהמשתמש לא מחובר
			try {
				const { useCartStore } = await import("./useCartStore");
				useCartStore.getState().setUserLoginStatus(false);
			} catch (error) {
				console.error('Error updating cart status:', error);
			}
			
			return null;
		}
	},

	refreshToken: async () => {
		// Prevent multiple simultaneous refresh attempts
		if (get().checkingAuth) return;

		set({ checkingAuth: true });
		try {
			const response = await authApi.refreshToken();
			set({ checkingAuth: false });
			return response;
		} catch (error) {
			clearAuthSessionMarker();
			set({ user: null, checkingAuth: false });
			try { get().stopTokenRefreshScheduler(); } catch { /* noop */ }
			throw error;
		}
	},

	// ריענון טוקן יזום: כל 12 דקות (ACCESS=15m)
	startTokenRefreshScheduler: () => {
		const state = get();
		if (!state.user) return;
		if (window.__shaShaTokenRefreshTimer) clearInterval(window.__shaShaTokenRefreshTimer);
		window.__shaShaTokenRefreshTimer = setInterval(() => {
			const s = get();
			if (!s.user) return;
			s.refreshToken().catch(() => {});
		}, 12 * 60 * 1000);
	},
	stopTokenRefreshScheduler: () => {
		if (window.__shaShaTokenRefreshTimer) {
			clearInterval(window.__shaShaTokenRefreshTimer);
			window.__shaShaTokenRefreshTimer = null;
		}
		if (window.__shaShaTokenRefreshKickoff) {
			clearTimeout(window.__shaShaTokenRefreshKickoff);
			window.__shaShaTokenRefreshKickoff = null;
		}
	},

	/**
	 * שליחת בקשת איפוס סיסמה
	 */
	forgotPassword: async (email) => {
		set({ loading: true });

		try {
			const response = await authApi.forgotPassword(email);
			set({ loading: false });
			toast.success(response.message || "נשלח מייל עם קישור לאיפוס סיסמה");
			return { success: true, message: response.message };
		} catch (error) {
			set({ loading: false });
			const message = error?.response?.data?.message || error?.message || "שגיאה בשליחת בקשת איפוס הסיסמה";
			toast.error(message);
			return { success: false, message };
		}
	},

	/**
	 * איפוס סיסמה עם טוקן
	 */
	resetPassword: async (token, password) => {
		set({ loading: true });

		try {
			const response = await authApi.resetPassword(token, password);
			set({ loading: false });
			toast.success(response.message || "הסיסמה אופסה בהצלחה!");
			return { success: true, message: response.message };
		} catch (error) {
			set({ loading: false });
			const message = error?.response?.data?.message || error?.message || "שגיאה באיפוס הסיסמה";
			toast.error(message);
			return { success: false, message };
		}
	},

	/**
	 * עדכון פרופיל משתמש
	 */
	updateProfile: async (profileData) => {
		set({ loading: true });

		try {
			const response = await authApi.updateProfile(profileData);
			
			if (response.success) {
				set({ user: response.user, loading: false });
				toast.success("הפרופיל עודכן בהצלחה!");
				return { success: true, user: response.user };
			}
		} catch (error) {
			set({ loading: false });
			const message = error?.response?.data?.message || error?.message || "שגיאה בעדכון הפרופיל";
			toast.error(message);
			return { success: false, message };
		}
	},

	/**
	 * שינוי סיסמה
	 */
	changePassword: async (currentPassword, newPassword) => {
		set({ loading: true });

		try {
			const res = await axios.post("/auth/change-password", {
				currentPassword,
				newPassword
			});
			
			if (res.data.success) {
				set({ loading: false });
				toast.success("הסיסמה שונתה בהצלחה!");
				return { success: true, message: res.data.message };
			}
		} catch (error) {
			set({ loading: false });
			const message = error?.response?.data?.message || error?.message || "שגיאה בשינוי הסיסמה";
			toast.error(message);
			return { success: false, message };
		}
	},

	/**
	 * קבלת היסטוריית הזמנות
	 */
	getUserOrders: async () => {
		try {
			const res = await axios.get("/auth/orders");
			return { success: true, orders: res.data.orders || [] };
		} catch (error) {
			const message = error?.response?.data?.message || error?.message || "שגיאה בקבלת היסטוריית הזמנות";
			return { success: false, message, orders: [] };
		}
	},

	/**
	 * קבלת רשימת מועדפים
	 */
	getWishlist: async () => {
		try {
			const res = await axios.get("/auth/wishlist");
			return { success: true, wishlist: res.data.wishlist || [] };
		} catch (error) {
			const message = error?.response?.data?.message || error?.message || "שגיאה בקבלת רשימת המועדפים";
			return { success: false, message, wishlist: [] };
		}
	},

	/**
	 * הוספה לרשימת מועדפים
	 */
	addToWishlist: async (productId) => {
		try {
			const res = await axios.post(`/auth/wishlist/${productId}`);
			
			if (res.data.success) {
				// עדכון המשתמש בסטור
				const { user } = get();
				if (user) {
					set({ 
						user: { 
							...user, 
							wishlist: res.data.wishlist 
						}
					});
				}
				toast.success("המוצר נוסף לרשימת המועדפים!");
				return { success: true };
			}
		} catch (error) {
			const message = error?.response?.data?.message || error?.message || "שגיאה בהוספה לרשימת המועדפים";
			toast.error(message);
			return { success: false, message };
		}
	},

	/**
	 * הסרה מרשימת מועדפים
	 */
	removeFromWishlist: async (productId) => {
		try {
			const res = await axios.delete(`/auth/wishlist/${productId}`);
			
			if (res.data.success) {
				// עדכון המשתמש בסטור
				const { user } = get();
				if (user) {
					set({ 
						user: { 
							...user, 
							wishlist: res.data.wishlist 
						}
					});
				}
				toast.success("המוצר הוסר מרשימת המועדפים!");
				return { success: true };
			}
		} catch (error) {
			const message = error?.response?.data?.message || error?.message || "שגיאה בהסרה מרשימת המועדפים";
			toast.error(message);
			return { success: false, message };
		}
	},

	/**
	 * בדיקה אם מוצר ברשימת המועדפים
	 */
	isInWishlist: (productId) => {
		const { user } = get();
		return user?.wishlist?.includes(productId) || false;
	},
}));

// TODO: Implement the axios interceptors for refreshing access token

// Axios interceptor for token refresh
let refreshPromise = null;

// השתמש באותו axios instance
axios.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;
		const userStore = useUserStore.getState();
		const requestUrl = originalRequest?.url || "";
		const isAuthRecoveryRequest =
			requestUrl.includes("/auth/refresh-token") ||
			requestUrl.includes("/auth/login") ||
			requestUrl.includes("/auth/signup") ||
			requestUrl.includes("/auth/logout");
		
		// אם שגיאת 401 והמשתמש לא מחובר, לא ננסה לרענן טוקן
		if (error?.response?.status === 401 && !originalRequest._retry && !isAuthRecoveryRequest) {
			originalRequest._retry = true;
			
			// אם המשתמש לא מחובר, פשוט נחזיר את השגיאה
			if (!userStore.user) {
				return Promise.reject(error);
			}

			try {
				// If a refresh is already in progress, wait for it to complete
				if (refreshPromise) {
					await refreshPromise;
					return axios(originalRequest);
				}

				// Start a new refresh process
				refreshPromise = userStore.refreshToken();
				await refreshPromise;
				refreshPromise = null;

				return axios(originalRequest);
			} catch (refreshError) {
				// If refresh fails, clear user state
				userStore.logout();
				refreshPromise = null;
				return Promise.reject(refreshError);
			}
		}
		return Promise.reject(error);
	}
);
